import React, { useCallback, useEffect, useMemo, useState } from "react";

import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
} from "@mui/material";
import Box from "@mui/system/Box/Box";

import { Group } from "@safetyculture/s12-apis-connect-web/s12/tools/support/v1/groups_pb";

import { regionValuesArr } from "../../api/index";
import { DEFAULT_REGION_CODE } from "../../constants";
import mainStyles from "../../styles";
import { GroupByRegion } from "../../types/permissions";
import ConfirmDialog from "../shared/confirm-dialog";
import DisplayError from "../shared/display-error";
import DisplayHeading from "../shared/display-heading";
import settingsStyles from "./styles";
import {
  createGroup,
  listGroups,
  removeGroup,
} from "api/endpoints/periscope-admin";
import { callOptionsHeaders } from "api/helpers";

const GroupAppSettings = () => {
  const classes = mainStyles();
  const settingsClasses = settingsStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const defaultRegion = useMemo(() => {
    return regionValuesArr.length === 1
      ? regionValuesArr[0]
      : DEFAULT_REGION_CODE;
  }, []);
  const [groupList, setGroupList] = useState<Group[]>([]);
  const [groupListByRegion, setGroupListByRegion] = useState<GroupByRegion>({
    us: {
      groupsList: [],
    },
    eu: {
      groupsList: [],
    },
    au: {
      groupsList: [],
    },
  });
  const [groupNameToBeAdded, setGroupNameToBeAdded] = useState<string>("");
  const [groupNameToBeRemoved, setGroupNameToBeRemoved] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState("");

  const getGroupsListReq = async () => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return listGroups(callOptionsHeaders(regionCode))
          .then((groupsListResp) => {
            groupsListResp.sort((groupA, groupB) =>
              groupA.name.localeCompare(groupB.name),
            );
            if (regionCode === defaultRegion) {
              setGroupList([...groupsListResp]);
            }
            const updatedGroupsByRegion = { ...groupListByRegion };
            updatedGroupsByRegion[regionCode].groupsList = [...groupsListResp];
            setGroupListByRegion(updatedGroupsByRegion);
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      }),
    );
    setIsLoading(false);
  };

  const addGroupReq = async () => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return createGroup(
          { name: groupNameToBeAdded.trimEnd() },
          callOptionsHeaders(regionCode),
        )
          .then((groupResp) => {
            // Since the groups list is the same for each region, we just show
            // default region groups list for display purposes
            if (groupResp && groupList && regionCode === defaultRegion) {
              const groupsListCopy = [...groupList];
              groupsListCopy.push(groupResp);
              setGroupList(groupsListCopy);
            }
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      }),
    );
    setIsLoading(false);
    setGroupNameToBeAdded("");
  };

  const removeGroupReq = async (groupName: string) => {
    setIsLoading(true);
    await Promise.all(
      regionValuesArr.map((regionCode) => {
        return removeGroup(
          { name: groupName.trimEnd() },
          callOptionsHeaders(regionCode),
        )
          .then(() => {
            getGroupList();
          })
          .catch((error) => {
            setErrorMessage(error.message);
          });
      }),
    );
    setIsLoading(false);
    setGroupNameToBeRemoved("");
  };

  const handleGroupNameEvents = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setGroupNameToBeAdded(event.target.value as string);
  };

  // eslint-disable-next-line
  const getGroupList = useCallback(getGroupsListReq, [defaultRegion]);
  useEffect(() => {
    getGroupList();
  }, [getGroupList]);

  return (
    <Grid
      container
      spacing={1}
      justifyContent="center"
      alignItems="center"
      direction="column"
      sx={classes.root}
    >
      <DisplayError errorMessage={errorMessage} />
      <Grid item xs={12}>
        <Box
          component="form"
          sx={settingsClasses.formSpacing}
          noValidate
          autoComplete="off"
        >
          <Box
            display="flex"
            alignItems="flex-start"
            gap={2}
            flexWrap="wrap"
            justifyContent="center"
          >
            <TextField
              id="group-name-input"
              label="Group Name"
              value={groupNameToBeAdded}
              onChange={(event) => handleGroupNameEvents(event)}
              variant="outlined"
              size="small"
              disabled={isLoading}
              sx={classes.formControl}
            />
            <Button
              variant="contained"
              color="primary"
              size="small"
              sx={{ minHeight: "40px" }}
              onClick={addGroupReq}
            >
              Add Group
            </Button>
          </Box>
        </Box>
        <Grid item xs={12}>
          <DisplayHeading>Existing Groups:</DisplayHeading>
        </Grid>
        <Grid item xs={12}>
          {isLoading ? (
            <CircularProgress
              color="primary"
              sx={settingsClasses.loadingSpinner}
              thickness={1}
              size={90}
            />
          ) : (
            <List sx={settingsClasses.list} dense component="div" role="list">
              {groupList.map((group: Group, index) => {
                return (
                  <ListItem
                    key={`group-item-${group.id}-${index}`}
                    role="listitem"
                  >
                    <ListItemText
                      id={`group-item-${group.id}-label`}
                      primary={group.name}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => {
                          setGroupNameToBeRemoved(group.name);
                          setOpenConfirmDialog(true);
                        }}
                        size="large"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
              <ListItem />
            </List>
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        title="Confirm Action"
        open={openConfirmDialog}
        setOpen={setOpenConfirmDialog}
        onConfirm={() => removeGroupReq(groupNameToBeRemoved)}
        cancelBtnLabel="Cancel"
        confirmBtnLabel="Confirm"
      >
        Are you sure you want to delete Group - {groupNameToBeRemoved}?
      </ConfirmDialog>
    </Grid>
  );
};

export default GroupAppSettings;
